<template>
  <div class="button-wrapper">
    <div
      class="button"
      :class="{'disabled': disabled || loading, 'sm': small}"
      @click.stop="handleClick"
    >
      <slot v-if="!loading" name="prefix"></slot>
      <span v-if="loading" class="bi bi-arrow-repeat" />
      <span v-if="label !== 'default'">{{ label }}</span>
      <i :class="{'rotate': state.dropdownToggled}" class="bi bi-caret-down-fill dropdown-icon"></i>
    </div>
    <div v-if="state.dropdownToggled && !loading && !disabled" class="dropdown">
      <div class="dropdown-item" :class="{'disabled': dropdownItem.disabled}" v-for="dropdownItem in dropdown" @click="!dropdownItem.disabled ? handleClickOnDropdownItem(dropdownItem) : null">
        <span>{{ dropdownItem.label }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.button-wrapper {
  position: relative;
  display: inline-block;

  .button {
    display: flex;
    gap: 8px;
    align-items: center;

    padding: 12px 24px;
    height: 46px;
    width: fit-content;
    border-radius: 4px;
    border: 1px solid var(--light-grey-color);
    font-family: $bioSans;
    font-size: 16px;
    background-color: var(--white-color);
    transition: background-color 0.3s, color 0.3s, border 0.3s;
    cursor: pointer;
    text-align: center;
    text-decoration: none;

    &.sm {
      height: 32px;
      font-size: 14px;
    }

    &:hover {
      background-color: $primary-100-color;
    }

    &.disabled {
      background-color: var(--button-disabled-background);
      color: var(--button-disabled-color);
      cursor: not-allowed;

      .bi-arrow-repeat {
        transform-origin: center;
        animation: rotation 2s infinite linear;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .dropdown-icon {
      font-size: 12px;

      &.rotate {
        transform: rotate(180deg);
      }
    }
  }

  .dropdown {
    position: absolute;
    width: 100%;
    background-color: var(--white-color);
    border: 1px solid var(--light-grey-color);
    border-radius: 4px;

    z-index: 5;
    top: calc(100% + 6px);
    left: 0px;

    padding: 0;
  }

  .dropdown-item {
    width: 100%;
    padding: 8px 12px;
    cursor: pointer;

    span {
      width: 100%;
    }

    &.disabled {
      background-color: var(--button-disabled-background) !important;
      color: var(--button-disabled-color);
      cursor: not-allowed;
    }

    &:hover {
      background-color: $white-600-color;
    }
  }
}
</style>

<script setup lang="ts">
const emits = defineEmits(["click"]);

const state = reactive({
  dropdownToggled: false,
});

export interface DropdownItem {
  id: string,
  label: string,
  disabled: boolean,
}

const {
  label = "default",
  icon,
  loading = false,
  disabled = false,
  dropdown,
  small = false,
} = defineProps<{
  label?: string,
  icon?: string,
  loading?: boolean,
  disabled?: boolean,
  dropdown?: DropdownItem[],
  small?: boolean,
}>();

function handleClick() {
  if (!disabled && !loading) {
    state.dropdownToggled = !state.dropdownToggled;
  }
}

function handleClickOnDropdownItem(dropdownItem: DropdownItem) {
  emits('click', dropdownItem);
  state.dropdownToggled = !state.dropdownToggled;
}
</script>
